import React, { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, SectionCard, StatusBadge, SignatureBadge, ProvenanceBadge, Mono, InfoNote } from "@/components/uikit";
import { generateEvidencePack } from "@/lib/evidencePack";
import { ORG } from "@/lib/constants";
import { toast } from "sonner";

export default function EvidencePack() {
  const { records, integrity, sigStatus, stats } = useApp();
  const [scope, setScope] = useState("chain");
  const [selectedId, setSelectedId] = useState(records[0]?.id || "");

  const selectedRecords = useMemo(() => {
    if (scope === "chain") return records;
    const r = records.find((x) => x.id === selectedId);
    return r ? [r] : [];
  }, [scope, records, selectedId]);

  const generate = () => {
    if (selectedRecords.length === 0) {
      toast.error("Nothing to export", { description: "No records available for the selected scope." });
      return;
    }
    const packId = generateEvidencePack({
      records: scope === "chain" ? records : selectedRecords,
      integrity,
      sigStatus,
      scopeLabel: scope === "chain" ? "Full evidence chain" : "Single record",
    });
    toast.success("Evidence Pack (PDF) generated", { description: packId });
  };

  return (
    <div>
      <PageHeader overline="Briefing-Ready Export" title="Evidence Pack (PDF)" icon="FileText"
        subtitle="Generate a professional PDF report of a single record or the entire cryptographic chain — for briefings, pilots, audits, compliance, and partner reviews." />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SectionCard title="Pack Scope" icon="Layers" testId="pack-scope-card">
          <div className="space-y-3">
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="pack-scope-select" className="border-slate-700 bg-slate-950 text-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900 text-slate-200">
                <SelectItem value="chain">Entire cryptographic chain ({records.length})</SelectItem>
                <SelectItem value="single">Single record</SelectItem>
              </SelectContent>
            </Select>
            {scope === "single" && (
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger data-testid="pack-record-select" className="border-slate-700 bg-slate-950 text-slate-200"><SelectValue placeholder="Choose a record" /></SelectTrigger>
                <SelectContent className="max-h-64 border-slate-700 bg-slate-900 text-slate-200">
                  {records.map((r, i) => (
                    <SelectItem key={r.id} value={r.id}>#{i} · {r.payload_type} · {r.event_id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button onClick={generate} className="w-full bg-cyan-500 font-bold text-slate-950 hover:bg-cyan-400 glow-cyan" data-testid="pack-generate-button">
              <Icons.Download className="mr-2 h-4 w-4" /> Generate PDF Evidence Pack
            </Button>
            <InfoNote tone="info">
              The pack documents <strong>locally generated</strong> cryptographic + analytical evidence. External
              certification / notarization / blockchain / RFC 3161 is represented only when independently performed.
            </InfoNote>
          </div>
        </SectionCard>

        <SectionCard title="Preview Summary" icon="Eye" className="lg:col-span-2" testId="pack-preview-card">
          <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Records" value={selectedRecords.length} />
            <Stat label="Verified" value={stats.verified} />
            <Stat label="Failures" value={stats.failures} />
            <Stat label="Chain" value={stats.failures > 0 ? "BROKEN" : "INTACT"} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-800 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Integrity</th>
                  <th className="py-2 pr-3">Signature</th>
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2">SHA-256</th>
                </tr>
              </thead>
              <tbody>
                {selectedRecords.map((r) => {
                  const gi = records.indexOf(r);
                  const ig = integrity[gi];
                  const ok = ig ? ig.selfOk && ig.linkOk : true;
                  return (
                    <tr key={r.id} className="border-b border-slate-900 text-slate-300" data-testid={`pack-row-${r.id}`}>
                      <td className="py-2 pr-3 font-mono text-slate-500">{gi}</td>
                      <td className="py-2 pr-3">{r.payload_type}</td>
                      <td className="py-2 pr-3"><StatusBadge status={ok ? "VERIFIED" : "WARNING"} /></td>
                      <td className="py-2 pr-3"><SignatureBadge state={sigStatus[r.id] || "UNSIGNED RECEIPT"} /></td>
                      <td className="py-2 pr-3"><ProvenanceBadge source={r.source_tag} /></td>
                      <td className="py-2"><Mono className="inline-block max-w-[120px] truncate align-middle">{r.sha256}</Mono></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-slate-600">{ORG.name} · CAGE {ORG.cage} · {ORG.crypto}</p>
        </SectionCard>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/50 p-3">
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="mt-1 font-heading text-xl font-bold text-slate-100">{value}</p>
    </div>
  );
}
